import apiClient from '../api/apiClient'
import type {
  ApiResponse,
  CreateUserRequest,
  User,
  UserResponse,
} from '../types/api'

const parseJsonWithUuidString = <T>(raw: string): T => {
  if (!raw) {
    return {} as T
  }

  const sanitised = raw.replace(
    /"user_uuid":\s*(\d+)/g,
    (_match, digits: string) => `"user_uuid":"${digits}"`,
  )

  try {
    return JSON.parse(sanitised) as T
  } catch (error) {
    console.error('Failed to parse user payload', error, sanitised)
    throw error
  }
}

const withUuidParser = <T>() => (raw: string) => parseJsonWithUuidString<T>(raw)

export class UserService {
  async getMe(): Promise<UserResponse> {
    const { data } = await apiClient.get<{ user: User; account?: any; education?: any[]; experience?: any[] }>(
      '/me',
      { transformResponse: [withUuidParser<{ user: User; account?: any; education?: any[]; experience?: any[] }>()] },
    )
    return {
      user: data.user,
      education: data.education ?? [],
      experience: data.experience ?? [],
    }
  }

  async getUser(userId: string | number): Promise<UserResponse> {
    const { data } = await apiClient.get<UserResponse>(
      `/admin/users/${userId}`,
      { transformResponse: [withUuidParser<UserResponse>()] },
    )
    return data
  }

  async updateUser(
    userId: string | number,
    userData: Partial<CreateUserRequest>,
  ): Promise<ApiResponse<User>> {
    const { data } = await apiClient.put<ApiResponse<User>>(
      `/admin/users/${userId}`,
      userData,
      { transformResponse: [withUuidParser<ApiResponse<User>>()] },
    )
    return data
  }
}

export const userService = new UserService()
